import Coordinates from './coordinates'
import GooglePlacePhoto from './googlePlacePhoto'
import { getFirstPhotoUrl } from '@/utils/photoUtils'

export interface GooglePlacesData {
  shortFormattedAddress?: string
  formattedAddress?: string
  location?: {
    latitude: number | { $numberDouble: string }
    longitude: number | { $numberDouble: string }
  }
  photos?: Array<{ name?: string; photo_reference?: string; url?: string }>
  addressComponents?: Array<{
    types?: string[]
    longText?: string
    shortText?: string
    long_name?: string
    text?: string
    label?: string
    long?: string
  }>
  address_components?: Array<{
    types?: string[]
    longText?: string
    shortText?: string
    long_name?: string
    text?: string
    label?: string
    long?: string
  }>
  [key: string]: unknown
}

export interface CityData {
  _id?: string
  short_name?: string
  long_name?: string
  identifying_name?: string
  coordinates?: Record<string, unknown>
  google_places_data?: GooglePlacesData
  photos?: GooglePlacePhoto[]
  [key: string]: unknown
}

export default class City {
  _id: string
  short_name: string
  long_name: string
  identifying_name: string // This is a long name we can use to uniquely ID the city.
  coordinates: Coordinates
  google_places_data?: GooglePlacesData

  photos: GooglePlacePhoto[] = [] // From Google Places data.

  constructor(data: CityData) {
    this._id = data._id ?? ''
    this.short_name = data.short_name ?? ''
    this.long_name = data.long_name ?? ''
    this.identifying_name = data.identifying_name ?? ''
    this.coordinates = new Coordinates(data.coordinates as Record<string, unknown> | undefined)
    this.google_places_data = data.google_places_data

    if (this.google_places_data && this.google_places_data.photos?.length) {
      this.photos = this.google_places_data.photos.map((photo) =>
        new GooglePlacePhoto(photo as Record<string, unknown>)
      )
    } else if (data?.photos?.length) {
      this.photos = data.photos.map((photo) =>
        new GooglePlacePhoto(photo as unknown as Record<string, unknown>)
      )
    }
  }

  get thumbnailPhotoURI(): string | undefined {
    // Use unified photo utility to extract photo URL from identifiers
    return getFirstPhotoUrl(
      {
        photos: this.photos,
        google_places_data: this.google_places_data,
      },
      512
    ) || undefined
  }

  get largePhotoURI(): string | undefined {
    // Use unified photo utility to extract photo URL from identifiers
    return getFirstPhotoUrl(
      {
        photos: this.photos,
        google_places_data: this.google_places_data,
      },
      1200
    ) || undefined
  }

  get country(): string | undefined {
    if (!this.google_places_data) return
    const sortedAddressComponents = City.sortedAddressComponents(
      this.google_places_data
    )
    return sortedAddressComponents.country ?? undefined
  }

  // Matches code in the backend app.
  static fromGooglePlacesData(google_places_data: GooglePlacesData) {
    const identifying_name =
      City.identifyingNameFromGooglePlacesData(google_places_data)

    const loc = google_places_data.location
    const lat = loc?.latitude
    const lng = loc?.longitude

    return new City({
      short_name: google_places_data.shortFormattedAddress ?? '',
      long_name: google_places_data.formattedAddress ?? '',
      identifying_name,
      coordinates: {
        latitude: typeof lat === 'number' ? lat : 0,
        longitude: typeof lng === 'number' ? lng : 0,
      },
      google_places_data
    })
  }

  static sortedAddressComponents(google_places_data: GooglePlacesData) {
    const components =
      google_places_data?.addressComponents ??
      google_places_data?.address_components ??
      []

    if (!Array.isArray(components) || components.length === 0) {
      return {}
    }

    return components.reduce((map: Record<string, string>, obj: { types?: string[]; longText?: string; long_name?: string; text?: string; label?: string; long?: string }) => {
      const types = Array.isArray(obj?.types) ? obj.types : []
      const primaryType = types[0]
      if (!primaryType) {
        return map
      }

      const longText =
        obj?.longText ??
        obj?.long_name ??
        obj?.text ??
        obj?.label ??
        obj?.long ??
        ''

      if (longText) {
        map[primaryType] = longText
      }
      return map
    }, {})
  }

  // Matches code in the backend.
  static identifyingNameFromGooglePlacesData(google_places_data: GooglePlacesData) {
    const sortedAddressComponents =
      City.sortedAddressComponents(google_places_data)

    const identifyingNameArray = [
      sortedAddressComponents.colloquial_area,
      sortedAddressComponents.locality,
      sortedAddressComponents.administrative_area_level_1,
      sortedAddressComponents.country
    ]
    return identifyingNameArray.filter(name => !!name).join(', ')
  }

  static cityNameFromGooglePlacesData(google_places_data: GooglePlacesData) {
    if (!google_places_data) return ''
    const sortedAddressComponents = City.sortedAddressComponents(google_places_data)
    return (
      sortedAddressComponents.locality ??
      sortedAddressComponents.colloquial_area ??
      ''
    )
  }
}
